import { omit } from 'lodash';
import { useContext } from 'react';
import { trackEvent } from '../analytics';
import { config } from '../config';
import { SocketContext } from '../contexts/SocketProvider';
import { CreditType } from '../interfaces/credit';
import { FeatureFlag } from '../interfaces/featureFlag';
import { isFeatureFlagEnabled } from '../utils';

export const useSocketEmit = () => {
  const { socket } = useContext(SocketContext);

  const getProfile = async () => {
    return new Promise((resolve, reject) => {
      socket.emit(config.socket.actions.GET_PROFILE, {}, (response) => {
        if (response.result?.success) {
          resolve(response.result.data);
        } else reject(response);
      });
    });
  };

  const getTotals = async () => {
    const isCourseCardsFeatureEnabled = isFeatureFlagEnabled(FeatureFlag.CourseCards);

    return new Promise((resolve, reject) => {
      socket.emit(config.socket.actions.GET_TOTALS, {}, (response) => {
        if (response.result?.success) {
          resolve(
            omit(
              {
                // TODO: For testing purpose, cleanup the comments when BE implements this
                // https://miamed.atlassian.net/browse/COEX-2602
                // [CreditType.CCCR]: {
                //   reviewed: 3,
                //   claimed: 67,
                // },
                // [CreditType.E2I]: {
                //   reviewed: 3,
                //   claimed: 67,
                // },
                ...response.result.data,
              },

              // Omit courses credit types if course_cards feature is not turned on
              isCourseCardsFeatureEnabled ? undefined : [CreditType.E2I, CreditType.CCCR],
            ),
          );
        } else reject(response);
      });
    });
  };

  const saveProfile = async (options) => {
    const opt = options;
    delete opt.acceptTerms;
    return new Promise((resolve, reject) => {
      socket.emit(config.socket.actions.SAVE_PROFILE, opt, (response) => {
        if (response.result?.success) {
          trackEvent({
            event: config.analytics.events.CME_ACCOUNT_SETUP_SAVED,
            options: {
              xid: response.result.data.profile.xid,
              clinical_role: response.result.data.profile.clinicalRole.value,
              cme_credit_type: opt.association,
            },
          });
          resolve(response.result.data);
        } else reject(response);
      });
    });
  };

  const getCredits = async (options) => {
    return new Promise((resolve, reject) => {
      socket.emit(config.socket.actions.GET_CREDITS, options, (response) => {
        if (response.result?.success) {
          resolve(response.result.data);
        } else reject(response);
      });
    });
  };

  const reviewCredit = async (options) => {
    return new Promise((resolve, reject) => {
      socket.emit(config.socket.actions.REVIEW_CREDIT, options, (response) => {
        if (response.result?.success) {
          resolve(response.result.data);
        } else reject(response);
      });
    });
  };

  const claimCredit = async (options) => {
    return new Promise((resolve, reject) => {
      socket.emit(config.socket.actions.CLAIM_CREDIT, options, (response) => {
        if (response.result?.success) {
          resolve(response.result.data);
        } else reject(response);
      });
    });
  };

  const parsResubmit = async (creditId) => {
    return new Promise((resolve, reject) => {
      socket.emit(config.socket.actions.PARS_RESUBMIT, { creditId }, (response) => {
        if (response.result?.success) {
          resolve(response.result.data);
        } else reject(response);
      });
    });
  };

  const getCourseDetails = async (courseId) => {
    return new Promise((resolve, reject) => {
      socket.emit(config.socket.actions.GET_COURSE_DETAILS, { courseId }, (response) => {
        if (response.result?.success) {
          resolve(response.result.data);
        } else reject(response);
      });
    });
  };

  return {
    socket,
    getProfile,
    saveProfile,
    getCredits,
    reviewCredit,
    claimCredit,
    parsResubmit,
    getTotals,
    getCourseDetails,
  };
};
